import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import FeaturedProducts from '../components/FeaturedProducts';

// ─── Hero Section ───
const HeroSection = () => {
  const videoRef = useRef(null);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [isMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);

  return (
    <section className="sty-hero">
      <div className="sty-hero-bg">
        {!isMobile && (
          <video ref={videoRef} src="/video/hero.mp4" autoPlay loop muted playsInline
            poster="/images/banners/hero-poster.jpg"
            preload="auto"
            onLoadedData={() => setVideoLoaded(true)}
            style={{ opacity: videoLoaded ? 1 : 0 }}
            aria-hidden="true">
            <track kind="captions" />
          </video>
        )}
        <picture>
          <source srcSet="/images/banners/hero-buste.webp" type="image/webp" />
          <img
            src="/images/banners/hero-buste-sm.jpg"
            alt=""
            className="sty-hero-fallback-img"
            fetchpriority="high"
            width="1280" height="720"
            style={{ opacity: videoLoaded ? 0 : 1 }}
          />
        </picture>
        <div className="sty-hero-overlay" />
      </div>
      <div className="sty-hero-content">
        <div className="sty-hero-text sty-fade-in">
          <p className="sty-overtitle sty-fade-in-delay1">CELLOPHANE & PAPER</p>
          <h1 className="sty-h1 sty-fade-in-delay2">
            L'eccellenza che<br />
            <span className="sty-accent">protegge</span> il tuo valore.
          </h1>
          <p className="sty-subtitle sty-fade-in-delay3">
            Soluzioni flessibili e trasparenza cristallina<br />per esaltare i tuoi prodotti.
          </p>
          <div className="sty-hero-btns sty-fade-in-delay4">
            <Link to="/prodotti" className="sty-btn-primary">Catalogo Prodotti</Link>
            <Link to="/contatti#contact-form" className="sty-btn-ghost">Richiedi Preventivo</Link>
          </div>
        </div>
        <div className="sty-scroll-indicator sty-fade-in-delay5" aria-hidden="true">
          <div className="sty-scroll-mouse"><div className="sty-scroll-wheel" /></div>
          <span>Scroll</span>
        </div>
      </div>
    </section>
  );
};


// ─── Stats ───
const StatsSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const stats = [
    { value: '2011', label: 'Anno di fondazione' },
    { value: '500+', label: 'Prodotti in catalogo' },
    { value: '80+', label: 'Pagine catalogo' },
  ];

  return (
    <section className="sty-stats" ref={ref}>
      <div className="sty-stats-grid">
        {stats.map((s, i) => (
          <motion.div key={i} className="sty-stat"
            initial={{ opacity: 0, y: 40 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: i * 0.15, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}>
            <div className="sty-stat-val">{s.value}</div>
            <div className="sty-stat-label">{s.label}</div>
          </motion.div>
        ))}
      </div>
    </section>
  );
};

// ─── Details ───
const DetailsSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const videoRef = useRef(null);
  const videoContainerRef = useRef(null);
  const videoInView = useInView(videoContainerRef, { amount: 0.5 });
  const [videoLoaded, setVideoLoaded] = useState(false);

  useEffect(() => {
    if (videoRef.current && videoInView) {
      if (!videoRef.current.src) {
        videoRef.current.src = '/video/detail.mp4';
        videoRef.current.load();
      }
      videoRef.current.play().catch(() => {});
    } else if (videoRef.current) {
      videoRef.current.pause();
    }
  }, [videoInView]);

  const specs = [
    'Idoneit\u00e0 totale al contatto alimentare',
    'Taglio di precisione millimetrica',
    'Igiene e freschezza garantite',
    'Materiali certificati M.O.C.A.',
    'Produzione 100% Made in Italy',
  ];

  return (
    <section className="sty-details" ref={ref}>
      <div className="sty-details-grid">
        <motion.div className="sty-details-text"
          initial={{ opacity: 0, x: -60 }}
          animate={isInView ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}>
          <span className="sty-badge">SPECIFICHE</span>
          <h2 className="sty-details-title">
            Specifiche Tecniche<br /><span className="sty-accent">Premium</span>
          </h2>
          <ul className="sty-details-list">
            {specs.map((spec, i) => (
              <motion.li key={i}
                initial={{ opacity: 0, x: -30 }}
                animate={isInView ? { opacity: 1, x: 0 } : {}}
                transition={{ delay: 0.3 + i * 0.12, duration: 0.7 }}>
                <div className="sty-bullet" /><span>{spec}</span>
              </motion.li>
            ))}
          </ul>
          <Link to="/prodotti" className="sty-details-cta">
            Esplora il catalogo
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
          </Link>
        </motion.div>
        <motion.div className="sty-details-video-wrap" ref={videoContainerRef}
          initial={{ opacity: 0, x: 60, scale: 0.95 }}
          animate={isInView ? { opacity: 1, x: 0, scale: 1 } : {}}
          transition={{ duration: 1, delay: 0.2 }}>
          <video ref={videoRef} muted playsInline loop preload="none"
            onLoadedData={() => setVideoLoaded(true)} className="sty-details-video"
            style={{ opacity: videoLoaded ? 1 : 0 }} aria-hidden="true">
            <track kind="captions" />
          </video>
          {!videoLoaded && (
            <div className="sty-details-fallback" style={{ backgroundImage: 'url(/images/banners/hero-dischi-sm.jpg)' }} />
          )}
        </motion.div>
      </div>
    </section>
  );
};

// ─── Products Marquee ───
const MarqueeSection = () => {
  const products = [
    { name: 'Buste PPL Cast', img: '/images/products/buste-ppl-cast-liscio-trasparenti-sm.jpg', section: 'alimentare' },
    { name: 'Dischi Hamburger', img: '/images/products/dischi-hamburger-ppl-bilaccato-sm.jpg', section: 'macelleria' },
    { name: 'Carta Forno', img: '/images/products/carta-forno-bisiliconata-sm.jpg', section: 'domestica' },
    { name: 'Alluminio Goffrato', img: '/images/products/alluminio-goffrato-box-sm.jpg', section: 'domestica' },
    { name: 'Truciolo Cellophane', img: '/images/products/truciolo-cellophane-colorato-sm.jpg', section: 'confezionamento' },
    { name: 'Film Poliolefinico', img: '/images/products/bobine-film-poliolefinico-sm.jpg', section: 'alimentare' },
    { name: 'Tovaglie Carta', img: '/images/products/tovaglie-carta-paglia-stampate-sm.jpg', section: 'domestica' },
    { name: 'Buste Fioristiche', img: '/images/products/buste-trasparenti-rose-sm.jpg', section: 'confezionamento' },
  ];

  return (
    <section className="sty-marquee">
      <motion.div className="sty-marquee-header"
        initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
        transition={{ duration: 0.8 }}>
        <span className="sty-badge">IL CATALOGO</span>
        <h2 className="sty-marquee-title" id="catalogo">I nostri prodotti</h2>
      </motion.div>
      <div className="sty-marquee-track">
        <div className="sty-marquee-inner">
          {[...products, ...products].map((p, i) => (
            <Link to={p.section ? `/prodotti/${p.section}` : '/prodotti'} key={i} className="sty-marquee-card">
              <div className="sty-marquee-card-img">
                <img src={p.img} alt={p.name} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              </div>
              <span className="sty-marquee-card-name">{p.name}</span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

// ─── Closing CTA ───
const ClosingSection = () => {
  const [videoLoaded, setVideoLoaded] = useState(false);
  const videoRef = useRef(null);
  const sectionRef = useRef(null);
  const closingInView = useInView(sectionRef, { once: true, margin: '200px' });

  useEffect(() => {
    if (closingInView && videoRef.current) {
      videoRef.current.src = '/video/closing.mp4';
      videoRef.current.load();
      videoRef.current.play().catch(() => {});
    }
  }, [closingInView]);

  return (
    <section className="sty-closing" ref={sectionRef}>
      <div className="sty-closing-bg">
        <video ref={videoRef} loop muted playsInline preload="none"
          onLoadedData={() => setVideoLoaded(true)} style={{ opacity: videoLoaded ? 1 : 0 }} aria-hidden="true">
          <track kind="captions" />
        </video>
        <div className="sty-closing-fallback"
          style={{ backgroundImage: 'url(/images/banners/hero-pellicola-sm.jpg)', opacity: videoLoaded ? 0 : 1 }} />
        <div className="sty-closing-overlay" />
      </div>
      <div className="sty-closing-content">
        <motion.h2 className="sty-closing-title"
          initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          transition={{ duration: 1 }}>
          Pronto a valorizzare<br />il tuo prodotto?
        </motion.h2>
        <motion.p className="sty-closing-desc"
          initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
          transition={{ delay: 0.4, duration: 0.8 }}>
          Contattaci per un preventivo gratuito e senza impegno.
        </motion.p>
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }} transition={{ delay: 0.7, duration: 0.8 }}>
          <Link to="/contatti#contact-form">
            <motion.button className="sty-closing-btn"
              whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.97 }}>
              Richiedi un Preventivo
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
            </motion.button>
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

// ─── Main ───
const Home = () => {
  return (
    <div className="sty-home">
      <HeroSection />
      <FeaturedProducts />
      <MarqueeSection />
      <StatsSection />
      <DetailsSection />
      <ClosingSection />
    </div>
  );
};

export default Home;
